import { useRef, useEffect } from 'react';
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  X,
  Info,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Code2,
  Rocket,
  Sparkles,
  GitBranch,
  Monitor,
} from 'lucide-react';
import { useNotificationStore, type Notification, type NotificationType } from '@/store/notificationStore';
import { cn } from '@/lib/utils';

/* ================================================================== */
/*  Icon + color mapping                                              */
/* ================================================================== */

function getTypeIcon(type: NotificationType) {
  switch (type) {
    case 'success': return CheckCircle2;
    case 'warning': return AlertTriangle;
    case 'error': return AlertCircle;
    default: return Info;
  }
}

function getTypeColor(type: NotificationType) {
  switch (type) {
    case 'success': return 'text-green-400';
    case 'warning': return 'text-yellow-400';
    case 'error': return 'text-red-400';
    default: return 'text-blue-400';
  }
}

function getSourceIcon(source?: string) {
  switch (source) {
    case 'compile': return Code2;
    case 'test': return CheckCircle2;
    case 'deploy': return Rocket;
    case 'ai': return Sparkles;
    case 'git': return GitBranch;
    default: return Monitor;
  }
}

/* ================================================================== */
/*  Single notification item                                          */
/* ================================================================== */

function NotificationItem({
  notification,
  onMarkRead,
  onRemove,
}: {
  notification: Notification;
  onMarkRead: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  const TypeIcon = getTypeIcon(notification.type);
  const typeColor = getTypeColor(notification.type);
  const SourceIcon = getSourceIcon(notification.source);

  return (
    <div
      className={cn(
        'flex gap-2 px-3 py-2 border-b border-[#1e1e1e] transition-colors group',
        notification.read ? 'opacity-60' : 'bg-[#1e1e1e]/40',
      )}
    >
      <TypeIcon className={cn('w-4 h-4 shrink-0 mt-0.5', typeColor)} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium text-[#d4d4d4] truncate">
            {notification.title}
          </span>
          {!notification.read && (
            <div className="w-1.5 h-1.5 rounded-full bg-[#007acc] shrink-0" />
          )}
        </div>
        {notification.message && (
          <p className="text-[10px] text-[#858585] mt-0.5 line-clamp-2">
            {notification.message}
          </p>
        )}
        <div className="flex items-center gap-1.5 mt-1">
          <SourceIcon className="w-2.5 h-2.5 text-[#666]" />
          <span className="text-[9px] text-[#666]">
            {formatTimeAgo(notification.timestamp)}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        {!notification.read && (
          <button
            onClick={() => onMarkRead(notification.id)}
            className="p-0.5 text-[#858585] hover:text-white rounded hover:bg-[#333]"
            title="Mark as read"
          >
            <Check className="w-3 h-3" />
          </button>
        )}
        <button
          onClick={() => onRemove(notification.id)}
          className="p-0.5 text-[#858585] hover:text-red-400 rounded hover:bg-[#333]"
          title="Remove"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Notifications Dropdown Panel                                      */
/* ================================================================== */

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NotificationPanel({ isOpen, onClose }: NotificationPanelProps) {
  const { notifications, unreadCount, markRead, markAllRead, removeNotification, clearAll } =
    useNotificationStore();
  const ref = useRef<HTMLDivElement>(null);

  // Click-outside to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={ref}
      className="absolute bottom-7 right-2 w-80 max-h-96 bg-[#252526] border border-[#454545] rounded-lg shadow-2xl z-50 flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#454545]">
        <div className="flex items-center gap-1.5">
          <Bell className="w-3.5 h-3.5 text-[#858585]" />
          <span className="text-xs font-medium text-[#d4d4d4]">Notifications</span>
          {unreadCount > 0 && (
            <span className="px-1.5 py-px text-[9px] bg-[#007acc] text-white rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="p-1 text-[#858585] hover:text-white rounded hover:bg-[#333]"
              title="Mark all as read"
            >
              <CheckCheck className="w-3.5 h-3.5" />
            </button>
          )}
          {notifications.length > 0 && (
            <button
              onClick={clearAll}
              className="p-1 text-[#858585] hover:text-red-400 rounded hover:bg-[#333]"
              title="Clear all"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Notification list */}
      <div className="flex-1 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-[#666]">
            <Bell className="w-8 h-8 mb-2 opacity-30" />
            <span className="text-xs">No notifications</span>
          </div>
        ) : (
          notifications.map((n) => (
            <NotificationItem
              key={n.id}
              notification={n}
              onMarkRead={markRead}
              onRemove={removeNotification}
            />
          ))
        )}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Notification Bell Button (for StatusBar)                          */
/* ================================================================== */

export function NotificationBell({
  onClick,
}: {
  onClick: () => void;
}) {
  const { unreadCount } = useNotificationStore();

  return (
    <button
      onClick={onClick}
      className="relative flex items-center gap-1 px-1.5 py-px rounded hover:bg-white/10 transition-colors"
      title={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
    >
      <Bell className="w-3 h-3" />
      {unreadCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 flex items-center justify-center text-[7px] font-bold bg-[#f14c4c] text-white rounded-full">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
}

/* ================================================================== */
/*  Helper                                                            */
/* ================================================================== */

function formatTimeAgo(timestamp: number): string {
  const diffMs = Date.now() - timestamp;
  const diffSecs = Math.floor(diffMs / 1000);
  if (diffSecs < 10) return 'just now';
  if (diffSecs < 60) return `${diffSecs}s ago`;
  const diffMins = Math.floor(diffSecs / 60);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return `${Math.floor(diffHrs / 24)}d ago`;
}
